import React from "react";
import environment from "web/script/modules/environment";
import CloseButton from "web/react/components/buttons/close-button/close-button";
import BranchLink from "web/react/components/branch-link/branch-link";
import { gettext } from "web/script/modules/django-i18n";
import classnames from "classnames";
import { BranchBannerCampaign } from "web/script/utils/app-banner-campaign";
import styles from "./small-app-download-banner.css";

interface SmallAppDownloadBannerProps {
    bannerCampaign: BranchBannerCampaign;
    onClose: () => void;
}

export const BANNER_DESCRIPTIONS = {
    // "Lyst is even better in the app"
    BETTER_IN_THE_APP: "app_download_small_banner.better_in_the_app.description",
    // "It's faster, personalized and at your fingertips."
    FASTER_PERSONALISED: "app_download_small_banner.faster_personalised.description",
    // "Get personalized results from your favourite brands"
    SEARCH_PERSONALISATION: "app_download_small_banner.search_personalisation.description",
    // "Track your favorite items and get sale alerts all in one place"
    TRACKING_SALES: "app_download_small_banner.tracking_sales.description",
};

const LARGE_DESCRIPTIONS = [
    BANNER_DESCRIPTIONS.SEARCH_PERSONALISATION,
    BANNER_DESCRIPTIONS.TRACKING_SALES,
];

function getBannerDescKey(campaignFlag: string): string {
    const bannerDescVariant = environment.getFeature(`${campaignFlag}_desc`);
    const descriptionKey = bannerDescVariant
        ? BANNER_DESCRIPTIONS[(bannerDescVariant as string).toUpperCase()]
        : BANNER_DESCRIPTIONS.BETTER_IN_THE_APP;

    return descriptionKey;
}

export function SmallAppDownloadBanner({
    bannerCampaign,
    onClose,
}: SmallAppDownloadBannerProps): JSX.Element {
    // "Download Lyst App"
    const bannerTitle = gettext("app_download_small_banner.general.title");
    // Open
    const ctaText = gettext("app_download_small_banner.general.cta");
    const bannerDescriptionKey = getBannerDescKey(bannerCampaign.flag);
    const bannerDescription = gettext(bannerDescriptionKey);
    const isLargeDescription = LARGE_DESCRIPTIONS.includes(bannerDescriptionKey);

    return (
        <>
            <div className={styles.placeholder} />
            <div className={classnames(styles.sticky, styles.downloadBanner)}>
                <div className={styles.wrapper}>
                    <CloseButton className={styles.closeButton} onClick={onClose} />
                    <div className={styles.appImage}>
                        <img src={bannerCampaign.bannerImageUrl} alt={"Lyst App"} />
                    </div>
                    <div
                        className={classnames(styles.content, {
                            [styles.largeContent]: isLargeDescription,
                        })}
                    >
                        <div className={styles.title}>{bannerTitle}</div>
                        <div className={styles.description}>{bannerDescription}</div>
                    </div>
                    <div className={styles.ctaContainer}>
                        <BranchLink
                            branchLink={bannerCampaign.branchLink}
                            className={styles.cta}
                            eventLabel={`app_banner_${bannerCampaign.name}`}
                            title={ctaText}
                            primaryButton
                        />
                    </div>
                </div>
            </div>
        </>
    );
}

export default SmallAppDownloadBanner;
